import { useTranslation } from 'react-i18next';
import classes from './VisitsCard.module.css';
import { formatTime } from '@utils/dateUtils';
import type { ObserveLocationVisits } from '@apis/reports';
import { PanelCard } from '../../shared/PanelCard/PanelCard';

const EMPTY = '—';

interface IVisitsCardProps {
  visits: ObserveLocationVisits | null;
  isLoading: boolean;
  tz: string;
  /**
   * When true, the card chrome (background + border-radius + shadow + padding)
   * is suppressed so the component can be nested inside a shared shell with
   * the histogram. The title and rows still render. Default false.
   */
  flush?: boolean;
}

function fmt(iso: string | null, lang: 'en' | 'ja', tz: string): string {
  if (!iso) return EMPTY;
  // Trusstor's observe-location uses lowercase am/pm (`12:08 am`); luxon's
  // English default emits uppercase, so lowercase the meridiem here. JP
  // formatting uses 24-h ("HH:mm") and is untouched.
  const formatted = formatTime(iso, lang, tz);
  return lang === 'en' ? formatted.replace(/\sAM$/i, ' am').replace(/\sPM$/i, ' pm') : formatted;
}

export function VisitsCard(props: IVisitsCardProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ja' ? 'ja' : 'en';
  const { visits, isLoading, tz, flush = false } = props;
  const title = t('reports.observeLocation.section.visits');

  if (isLoading || !visits) {
    return (
      <PanelCard flush={flush} aria-busy="true" aria-label={title}>
        <h4 className={classes.title}>{title}</h4>
        <div className={classes.skeleton} />
        <div className={classes.skeleton} />
        <div className={classes.skeleton} />
      </PanelCard>
    );
  }

  const busiest = visits.busiestTime
    ? `${fmt(visits.busiestTime.startIso, lang, tz)} – ${fmt(visits.busiestTime.endIso, lang, tz)}`
    : EMPTY;

  return (
    <PanelCard flush={flush} aria-label={title}>
      <h4 className={classes.title}>{title}</h4>
      <div className={classes.row}>
        <span className={classes.label}>{t('reports.observeLocation.visits.firstEntered')}</span>
        <span>{fmt(visits.firstEnteredIso, lang, tz)}</span>
      </div>
      <div className={classes.row}>
        <span className={classes.label}>{t('reports.observeLocation.visits.lastLeave')}</span>
        <span>{fmt(visits.lastLeftIso, lang, tz)}</span>
      </div>
      <div className={classes.row}>
        <span className={classes.label}>{t('reports.observeLocation.visits.busiest')}</span>
        <span>{busiest}</span>
      </div>
    </PanelCard>
  );
}
